import { channelApi } from '../../services/api'
import { getSocket } from '../../services/socket'
import type { ParticipantTarget, ParticipantCtx } from '../../stores/participantActionsStore'

/** Ban duration options. `label` = verbose (menu), `short` = compact (inline grid). minutes:null = permanent. */
export const BAN_DURATIONS: { label: string; short: string; minutes: number | null }[] = [
  { label: '5 min', short: '5m', minutes: 5 },
  { label: '10 min', short: '10m', minutes: 10 },
  { label: '15 min', short: '15m', minutes: 15 },
  { label: '30 min', short: '30m', minutes: 30 },
  { label: '1 hour', short: '1h', minutes: 60 },
  { label: 'Permanent', short: 'Perm', minutes: null },
]

/** Ban a participant. Sends userId for authed (USER) subjects, raw identity for guest/phone. */
export function banParticipant(ctx: ParticipantCtx, target: ParticipantTarget, minutes: number | null, reason: string) {
  const isUser = target.subjectType === 'USER'
  return channelApi.banUser(ctx.channelId, {
    userId: isUser ? target.userId : undefined,
    identity: isUser ? undefined : target.identity,
    subjectType: target.subjectType,
    displayName: target.displayName,
    minutes,
    reason: reason.trim() || undefined,
  })
}

/**
 * Kick a participant. kickUser accepts a userId for authed users OR a raw
 * identity (guest_<id> / phone:<num>) for guests/PSTN — the server resolves
 * either, so passing `userId ?? identity` is correct for both.
 */
export function kickParticipant(ctx: ParticipantCtx, target: ParticipantTarget) {
  return channelApi.kickUser(ctx.channelId, target.userId ?? target.identity)
}

/**
 * Admin "Move to current room": pull the target from the channel they're
 * currently in (`ctx.channelId`) into the admin's own voice room
 * (`currentChannelId`). moveUser's signature is (fromChannelId, userId,
 * targetChannelId) — order matters, flipping it would move the admin.
 * Only USER subjects can be moved (the endpoint is keyed by userId), so
 * call sites must gate this on `target.userId` being present.
 */
export function moveParticipantToCurrentRoom(
  ctx: ParticipantCtx,
  target: ParticipantTarget,
  currentChannelId: string,
) {
  return channelApi.moveUser(ctx.channelId, target.userId!, currentChannelId)
}

/** Courtesy-mute a participant now (they can unmute themselves). USER targets only. */
export function muteParticipant(ctx: ParticipantCtx, target: ParticipantTarget) {
  if (!target.userId) return
  getSocket().emit('voice:mute-participant', { channelId: ctx.channelId, targetUserId: target.userId })
}

/** Lock/unlock a participant's mic (force-mute). USER targets only. */
export function setForceMute(ctx: ParticipantCtx, target: ParticipantTarget, locked: boolean) {
  if (!target.userId) return
  getSocket().emit('voice:force-mute', { channelId: ctx.channelId, targetUserId: target.userId, locked })
}
